import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { Vehicle } from "../../types/vehicle";
import { vehicleImageUrl } from "../../utils/vehicleImage";

const DetailRow = ({ label, value }: { label: string; value: string | boolean | null }) => (
  <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
      {typeof value === "boolean" ? (value ? "Active" : "Inactive") : (value || "—")}
    </dd>
  </div>
);

export default function VehicleDetail() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [vehicleTypeName, setVehicleTypeName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  const fetchVehicleData = async () => {
    try {
      const response = await axiosInstance.get(`/vehicles/${id}`);
      const data = response.data as Vehicle;
      setVehicle(data);
      if (data.vehicleTypeId) {
        try {
          const typeRes = await axiosInstance.get(`/vehicletypes/${data.vehicleTypeId}`);
          const type = typeRes.data as { name?: string | null; description?: string | null };
          setVehicleTypeName(type.name?.trim() || type.description?.trim() || null);
        } catch {
          setVehicleTypeName(null);
        }
      } else {
        setVehicleTypeName(null);
      }
    } catch (fetchError) {
      setError("Failed to fetch vehicle data");
      console.error("Error fetching vehicle:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
        {error}
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center text-gray-500">
        Vehicle not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Vehicle Details" />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/vehicles/${vehicle.id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit Vehicle
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Vehicle Information</h3>
        </div>
        <div className="border-t border-gray-200">
          {vehicle.imageName && vehicleImageUrl(vehicle.imageName) && (
            <div className="px-6 py-5 border-b border-gray-200">
              <img
                src={vehicleImageUrl(vehicle.imageName)!}
                alt={vehicle.registration}
                className="h-48 w-48 rounded-xl border border-gray-200 object-cover"
                onError={(e) => {
                  if (!e.currentTarget.src.includes("placeholder-image.png")) {
                    e.currentTarget.src = "/images/placeholder-image.png";
                  }
                }}
              />
            </div>
          )}
          <dl>
            <DetailRow label="Registration" value={vehicle.registration} />
            <DetailRow label="Make" value={vehicle.make} />
            <DetailRow label="Model" value={vehicle.model} />
            <DetailRow label="Color" value={vehicle.color} />
            <DetailRow label="Vehicle Type" value={vehicleTypeName ?? vehicle.vehicleTypeId} />
            <DetailRow label="Driver ID" value={vehicle.driverId} />
            <DetailRow label="Status" value={vehicle.status} />
          </dl>
        </div>
      </div>
    </div>
  );
}
